/**
 * Hook personalizado para caché inteligente de imágenes de mensajes.
 * Implementa caché con localStorage para optimizar carga de imágenes de chat.
 * Evita consultas repetidas a Supabase para obtener URLs de imágenes.
 * 
 * @param {string} messageId - ID del mensaje
 * @returns {Object} - Objeto con imageUrl, loading, error, etc.
 */

import { useState, useEffect, useCallback } from 'react';
import { chatService } from '../services/chatService';

// Set global para tracking de imágenes precargadas exitosamente (evita duplicaciones)
const preloadedImages = new Set();

// Set global para tracking de imágenes que fallaron (evita reintentos infinitos)
const failedImages = new Set();

// Map global para tracking de precargas en curso (evita condiciones de carrera)
const loadingImages = new Map();

// Map global para caché de URLs en memoria (más rápido que localStorage)
const memoryCache = new Map();

/**
 * Genera una clave de caché única para la imagen del mensaje
 * @param {string} messageId - ID del mensaje
 * @returns {string} - Clave de caché
 */
const getCacheKey = (messageId) => {
  if (!messageId) return null;
  return `message_image_${messageId}`;
};

/**
 * Hook principal para caché de imágenes de mensajes
 */
export function useCachedMessageImage(messageId) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const cacheKey = getCacheKey(messageId);

  /**
   * Obtiene la URL de la imagen desde caché o Supabase
   */
  const fetchImageUrl = useCallback(async () => {
    if (!messageId) return;

    const loadKey = `load_${messageId}`;

    // Verificar si ya está cargando actualmente (evita condiciones de carrera)
    if (loadingImages.has(loadKey)) {
      console.log(`[useCachedMessageImage] Imagen ya está cargando, esperando: ${messageId}`);
      return;
    }

    // Verificar si ya está en caché de memoria
    if (memoryCache.has(messageId)) {
      console.log(`[useCachedMessageImage] URL encontrada en caché de memoria: ${messageId}`);
      setImageUrl(memoryCache.get(messageId));
      setLoading(false);
      return;
    }

    // Verificar si ya está en localStorage
    try {
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const { url, timestamp } = JSON.parse(cachedData);
        // Caché válido por 1 semana (604800000ms)
        const CACHE_TTL = 604800000;
        if (Date.now() - timestamp < CACHE_TTL) {
          console.log(`[useCachedMessageImage] URL encontrada en localStorage: ${messageId}`);
          memoryCache.set(messageId, url);
          setImageUrl(url);
          setLoading(false);
          return;
        } else {
          // Caché expirado, limpiarlo
          localStorage.removeItem(cacheKey);
          console.log(`[useCachedMessageImage] Caché expirado, limpiando: ${messageId}`);
        }
      }
    } catch (err) {
      console.error(`[useCachedMessageImage] Error leyendo caché localStorage:`, err);
    }

    // Verificar si ya falló anteriormente para evitar reintentos infinitos
    if (failedImages.has(messageId)) {
      console.log(`[useCachedMessageImage] Imagen falló anteriormente, saltando: ${messageId}`);
      setError(new Error('Imagen falló anteriormente'));
      setLoading(false);
      return;
    }

    // Marcar como en curso
    loadingImages.set(loadKey, true);
    
    try {
      console.log(`[useCachedMessageImage] Obteniendo URL de Supabase: ${messageId}`);
      
      setLoading(true);
      setError(null);

      const url = await chatService.getPersistentImageUrl(messageId);
      
      console.log(`[useCachedMessageImage] URL recibida de chatService: ${messageId}`, url);
      console.log(`[useCachedMessageImage] Actualizando estado con URL: ${messageId}`);
      
      // Guardar en caché de memoria
      memoryCache.set(messageId, url);
      
      // Guardar en localStorage
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          url,
          timestamp: Date.now()
        }));
      } catch (err) {
        console.error(`[useCachedMessageImage] Error guardando en localStorage:`, err);
      }

      // Marcar como precargado exitosamente
      preloadedImages.add(messageId);
      
      setImageUrl(url);
      setLoading(false);
    } catch (err) {
      console.error(`[useCachedMessageImage] Error obteniendo URL: ${messageId}`, err);
      
      // Marcar como fallido para evitar reintentos infinitos
      failedImages.add(messageId);
      
      setError(err);
      setLoading(false);
    } finally {
      // Liberar el flag de carga
      loadingImages.delete(loadKey);
    }
  }, [messageId, cacheKey]);

  /**
   * Carga la imagen cuando el messageId cambia
   */
  useEffect(() => {
    if (!messageId) {
      setImageUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    fetchImageUrl();
  }, [messageId, fetchImageUrl]);

  /**
   * Invalida el caché para una imagen específica
   * Útil cuando se actualiza la imagen del mensaje
   */
  const invalidateCache = useCallback(() => {
    if (!cacheKey || !messageId) return;
    
    console.log(`[useCachedMessageImage] Invalidando caché: ${cacheKey}`);
    
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error(`[useCachedMessageImage] Error invalidando caché:`, error);
    }
    
    // Limpiar caché de memoria
    memoryCache.delete(messageId);
    
    // Limpiar sets globales para permitir reintentos
    preloadedImages.delete(messageId);
    failedImages.delete(messageId);
    
    // Limpiar todos los loading keys para este messageId
    for (const key of loadingImages.keys()) {
      if (key.startsWith(`load_${messageId}`)) {
        loadingImages.delete(key);
      }
    }
    
    console.log(`[useCachedMessageImage] Caché invalidado para: ${messageId}`);
    
    // Recargar la imagen
    fetchImageUrl();
  }, [cacheKey, messageId, fetchImageUrl]);

  return {
    imageUrl,
    loading,
    error,
    invalidateCache,
  };
}

/**
 * Limpia todos los cachés globales de imágenes de mensajes
 * Útil para debugging o para resetear el estado completamente
 */
export const clearAllMessageImageCaches = () => {
  console.log('[useCachedMessageImage] Limpiando todos los cachés globales');
  preloadedImages.clear();
  failedImages.clear();
  loadingImages.clear();
  memoryCache.clear();
};

/**
 * Precarga imágenes de mensajes en lote
 * Útil para precargar imágenes de un chatroom antes de mostrarlas
 */
export const preloadMessageImages = async (messageIds) => {
  console.log(`[useCachedMessageImage] Precargando ${messageIds.length} imágenes en lote`);
  
  const promises = messageIds.map(async (messageId) => {
    try {
      const cacheKey = getCacheKey(messageId);
      
      // Verificar si ya está en caché
      if (memoryCache.has(messageId)) {
        return;
      }
      
      try {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const { url, timestamp } = JSON.parse(cachedData);
          const CACHE_TTL = 604800000;
          if (Date.now() - timestamp < CACHE_TTL) {
            memoryCache.set(messageId, url);
            return;
          }
        }
      } catch (err) {
        // Ignorar errores de localStorage
      }
      
      // Obtener URL de Supabase
      const url = await chatService.getPersistentImageUrl(messageId);
      memoryCache.set(messageId, url);
      
      // Guardar en localStorage
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          url,
          timestamp: Date.now()
        }));
      } catch (err) {
        // Ignorar errores de localStorage
      }
      
      preloadedImages.add(messageId);
    } catch (error) {
      console.error(`[useCachedMessageImage] Error precargando imagen ${messageId}:`, error);
      failedImages.add(messageId);
    }
  });
  
  await Promise.allSettled(promises);
  console.log(`[useCachedMessageImage] Precarga de lote completada`);
};
